/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  Vendors.js
Author:    jdkohl
Version:   0.03
Description:
Manage vendor relationships - allows customers to authorise vendors
to book freight on their behalf using whitelisted addresses.

History:
0.03  11-02-26 JDK   Added ReactRouterPrompt to warn on route navigation with unsaved changes
0.02  28-10-25 JDK   Added unsaved changes tracking and warning system
                      Added remove vendor functionality with confirmation
                      Added empty state UI with helpful message
                      Auto-select vendor after adding
0.01  25-08-25 JDK   Initial implementation
0.00  18-08-25 JDK   Created.
----------------------------------- */


import { useEffect, useReducer, useState, useCallback } from 'react';
import { omnisUrl } from '../../Configuration/Config';
import CardForm from '../../Common/CardForm';
import useAuthenticate from '../../Session/useAuthenticate';
import useNavigate from '../../Navigation/useNavigate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import AddVendor from './AddVendor/AddVendor';
import EditVendor from './EditVendor';
import DuplicateVendor from './DuplicateVendor';
import ReactRouterPrompt from 'react-router-prompt';
import Modal from '../../Navigation/Modal';
import '../Settings.css'


const allowedActions = {
  view: 'view',
  addVendor: 'addVendor'
}
class VendorViewState {
  constructor() {
    this.action = '';
    this.myAddresses = [];
    this.selectedVendor = null;
    this.vendors = [];
    this.error = '';
    this.hasChanges = false;
    this.originalVendor = null;
  }
}

export class Vendor {
  constructor({
    id = '',
    name = '',
    code = '',
    addresses = [],
    activated = false,
    permissions = { allowPickup: false, allowDropFreightIn: false },
    termsAccepted = false,
    termsAcceptanceTS = '',
  }) {
    this.id = id;
    this.name = name;
    this.email = '';
    this.code = code;
    this.addresses = addresses; // list of address ids only
    this.activated = activated;
    this.permissions = permissions;
    this.termsAccepted = termsAccepted;
    this.termsAcceptanceTS = termsAcceptanceTS
  }
}

function stateReducer(vendorViewState = new VendorViewState(), action) {
  if (Object.hasOwn(vendorViewState, action.type)) {
    return { ...vendorViewState, [action.type]: action.value }
  } else if (action.type === 'load') {
    return { ...vendorViewState, ...action.value }
  }
  return { ...vendorViewState };
}

const initialViewState = new VendorViewState();

function Vendors() {
  // const location = useLocation();
  const { loggedIn, customer, authModal } = useAuthenticate();
  const { changeType } = useNavigate();
  const [viewState, dispatchStateChange] = useReducer(stateReducer, initialViewState);                                 // When we're done, we use this to go back to the dashboard.
  const [addVendorState, setAddVendorState] = useState({
    canProceed: false,
    buttonText: 'Next',
    onNext: null
  });

  /**
   * Stable reference to error handler to prevent infinite loop.
   */
  const handleError = useCallback((error) => {
    dispatchStateChange({
      type: 'error',
      value: error
    })
  }, [])

  /**
   * Handle successful vendor addition from AddVendor workflow
   */
  const handleVendorAdded = useCallback((newVendor) => {
    // Add to list
    const vendors = [...viewState.vendors, newVendor];
    const newVendorIndex = vendors.length - 1;

    // Update state
    dispatchStateChange({ type: 'vendors', value: vendors });
    dispatchStateChange({ type: 'selectedVendor', value: newVendorIndex });
    dispatchStateChange({ type: 'action', value: allowedActions.view });

    // Initialize change tracking
    dispatchStateChange({
      type: 'originalVendor',
      value: { ...newVendor, addresses: [...newVendor.addresses], permissions: { ...newVendor.permissions } }
    });
    dispatchStateChange({ type: 'hasChanges', value: false });
  }, [viewState.vendors]);

  let controls = {};

  if (viewState.action === allowedActions.view) {
    // EditVendor controls
    controls = {
      next: {
        call: (enabled) => {
          if (enabled) updateVendor(viewState.vendors[viewState.selectedVendor]);
        },
        name: 'Save',
        enable: viewState.selectedVendor !== null && viewState.hasChanges
      }
    };
  } else if (viewState.action === allowedActions.addVendor) {
    // AddVendor controls
    controls = {
      next: {
        call: addVendorState.onNext,
        name: addVendorState.buttonText,
        enable: addVendorState.canProceed
      }
    };
  }

  /**
   * Update vendor settings (authorisation status and whitelisted addresses)
   * After successful update:
   * - Clears the hasChanges flag
   * - Updates the originalVendor baseline for future change detection
   * @param {Object} vendor - The vendor object with updated properties
   */
  async function updateVendor(vendor) {
    try {
      let response = await fetch(`${omnisUrl}/vendor`, {
        method: "PATCH",
        body: JSON.stringify({ token: customer?.token, vendor })
      });
      if (response.ok) {
        // Clear the changes flag and update original vendor after successful save (deep copy)
        dispatchStateChange({ type: 'hasChanges', value: false });
        dispatchStateChange({ type: 'originalVendor', value: { ...vendor, addresses: [...vendor.addresses], permissions: { ...vendor.permissions } } });
      } else {
        dispatchStateChange({ type: 'error', value: response.statusText });
      }
    } catch (error) {
      dispatchStateChange({ type: 'error', value: error.message });
    }
  }

  /**
   * Remove a vendor from the customer's vendor list
   * After successful removal:
   * - Removes vendor from the list
   * - Clears selection and resets all tracking state
   * @param {Object} vendor - The vendor object to remove
   */
  async function removeVendor(vendor) {
    try {
      let params = new URLSearchParams({ token: customer?.token, id: vendor.id });
      let response = await fetch(`${omnisUrl}/vendor?${params}`, {
        method: "DELETE"
      });
      if (response.ok) {
        // Remove vendor from the list
        const updatedVendors = viewState.vendors.filter((_, idx) => idx !== viewState.selectedVendor);
        dispatchStateChange({ type: 'vendors', value: updatedVendors });

        // Clear selection and reset state
        dispatchStateChange({ type: 'selectedVendor', value: null });
        dispatchStateChange({ type: 'action', value: '' });
        dispatchStateChange({ type: 'hasChanges', value: false });
        dispatchStateChange({ type: 'originalVendor', value: null });
      } else {
        dispatchStateChange({ type: 'error', value: response.statusText });
      }
    } catch (error) {
      dispatchStateChange({ type: 'error', value: error.message });
    }
  }

  /**
   * Show confirmation dialog before removing a vendor
   * Displays vendor name and warns that action cannot be undone
   */
  const handleRemoveVendor = () => {
    if (viewState.selectedVendor === null) return;

    const vendor = viewState.vendors[viewState.selectedVendor];

    if (vendor.code === 'DUPLICATE')
      removeVendor(vendor);
    else
      authModal({
        title: 'Remove Vendor',
        description: `Are you sure you want to remove ${vendor.name}? This action cannot be undone.`,
        no: {
          name: 'Cancel',
          call: () => {
            // Do nothing, just close modal
          }
        },
        yes: {
          name: 'Remove',
          call: () => {
            removeVendor(vendor);
          }
        }
      });
  }

  /**
   * Check for unsaved changes before navigation
   * If changes exist, shows modal with options to:
   * - Save: Saves changes then executes the navigation action
   * - Discard: Restores original data then executes the navigation action
   * - Cancel: Closes modal and stays on current vendor
   * @param {Function} action - The navigation action to execute after handling changes
   * @returns {boolean} - True if safe to proceed, false if modal was shown
   */
  const checkUnsavedChanges = (action) => {
    if (viewState.hasChanges && viewState.originalVendor) { // has changes & originalVendor is not falsy = editing existing vendor record.
      authModal({
        title: 'Unsaved Changes',
        description: 'You have unsaved changes. Do you want to save them before continuing?',
        no: {
          name: 'Discard',
          call: () => {
            // Restore original vendor data (deep copy)
            if (viewState.originalVendor && viewState.selectedVendor !== null) {
              const updatedVendors = [...viewState.vendors];
              const original = viewState.originalVendor;
              updatedVendors[viewState.selectedVendor] = { ...original, addresses: [...original.addresses], permissions: { ...original.permissions } };
              dispatchStateChange({ type: 'vendors', value: updatedVendors });
            }
            dispatchStateChange({ type: 'hasChanges', value: false });

            // Execute the pending action
            action();
          }
        },
        yes: {
          name: 'Save',
          call: async () => {
            // Save the changes first, then execute the action
            await updateVendor(viewState.vendors[viewState.selectedVendor]);
            action();
          }
        }
      });

      return false;
    } else if (viewState.hasChanges && viewState.originalVendor === null) {
      authModal({
        title: "Cancel New Vendor",
        description: "If you leave your changes will be lost. Are you sure you want to continue?",
        no: {
          name: "Discard",
          call: action,
        },
        yes: {
          name: "Continue",
          call: () => { },
        }
      })
      return false;
    }
    return true;
  }

  // on first load, set the navigation type and fetch the users
  useEffect(() => {
    changeType('default', 'Vendors'); // LD0011667, the second string governs the display name in the header. JDK 02-11-22
    if (loggedIn) {
      const fetchVendorData = async () => {
        try {
          let response = await fetch(`${omnisUrl}/vendor?token=${customer?.token}`);
          if (response.ok) {
            let processedJSON = await response.json(); // returns object with two properties, vendors and addresses. Both properties are object arrays.
            // Map the fetched vendor data to Vendor objects and update state
            const vendors = processedJSON.vendors?.map(vendor => new Vendor({
              id: vendor.id,
              name: vendor.name,
              code: vendor.code,
              addresses: vendor.addresses || [],
              activated: vendor.activated || false,
              permissions: vendor.permissions || { allowPickup: false, allowDropFreightIn: false }
            })) || [];
            vendors.sort((a, b) => { // natural sort on name.
              return a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            })
            const myAddresses = processedJSON.addresses;
            dispatchStateChange({ type: 'load', value: { myAddresses, vendors } })
            // dispatchStateChange({ type: 'vendors', value: vendors });
            // dispatchStateChange({ type: 'myAddresses', value: addresses });
          } else {
            handleError(response.statusText);
            // dispatchStateChange({ type: 'error', value: response.statusText });
          }
        } catch (error) {
          handleError(error.message);
          // dispatchStateChange({ type: 'error', value: error.message });
        }
      }
      fetchVendorData();
    }
  }, [changeType, loggedIn, customer?.token, handleError]);

  const selectDetailView = function (action) {
    switch (action) {
      case allowedActions.addVendor:
        return (
          <AddVendor
            myAddresses={viewState.myAddresses}
            onVendorAdded={handleVendorAdded}
            onValidationChange={setAddVendorState}
            onError={handleError}
          />
        )

      case allowedActions.view:
        let vendors = viewState.vendors;
        let index = viewState.selectedVendor;
        // console.log(viewState);
        if (vendors[index]?.code === 'DUPLICATE')
          return <DuplicateVendor selectedVendor={vendors[index]} vendors={vendors} />;
        else
          return <EditVendor viewState={viewState} dispatchStateChange={dispatchStateChange} />;

      default:
        return <></>
    }
  }

  return (
    <CardForm
      title='My Vendors'
      controls={controls}
      className="margin-block-start user-form"
      error={viewState.error}
    >
      <div className='flex_container' >
        <div className="stack" style={{ minInlineSize: "25ch" }}>
          <p className='h3 table-heading' style={{ textAlign: "center" }}>Vendor</p>
          <div className='stack my-users'>
            {viewState?.vendors?.length === 0 ? (
              <div style={{ padding: "2rem", color: "var(--clr-neutral-700)", display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <div style={{ textAlign: "center" }}>
                  No vendors added yet.
                  <br />
                  Click the + button below to add a vendor.
                </div>
              </div>
            ) : (
              viewState?.vendors?.reduce((vendors, thisVendor, index) => {
                return [
                  ...vendors,
                  <button type="button"
                    key={`vendor_${index}`}
                    className={index === viewState?.selectedVendor ? 'checked btn' : 'not clicked btn'}
                    onClick={() => {
                      if (index !== viewState.selectedVendor) {
                        const action = () => {
                          dispatchStateChange({ type: 'action', value: allowedActions.view });
                          dispatchStateChange({ type: 'selectedVendor', value: index });
                          // Store original vendor data when selecting (deep copy)
                          const vendor = viewState.vendors[index];
                          dispatchStateChange({ type: 'originalVendor', value: { ...vendor, addresses: [...vendor.addresses], permissions: { ...vendor.permissions } } });
                          dispatchStateChange({ type: 'hasChanges', value: false });
                        };

                        if (checkUnsavedChanges(action)) {
                          action();
                        }
                      }
                    }}
                  >{`${thisVendor?.name}`}</button>
                ]
              }, [])
            )}
          </div>
        </div>
        <div className='box stack user-form'>
          {selectDetailView(viewState?.action)}
        </div>
      </div>
      <div className='cluster' style={{ marginInlineStart: "1.125rem" }}>
        <AddVendorButton
          clickHandler={() => {
            const action = () => {
              dispatchStateChange({ type: 'action', value: allowedActions.addVendor });
              dispatchStateChange({ type: 'selectedVendor', value: null });
              dispatchStateChange({ type: 'hasChanges', value: true });
              dispatchStateChange({ type: 'originalVendor', value: null });
            };

            if (checkUnsavedChanges(action)) {
              action();
            }
          }}
          action={viewState?.action}
        />
        {/* <button title="Add Vendor" // title={can.add ? 'Create new user' : 'Create new user - no access' }
          className="btn btn__add"
          type='button'
          onClick={() => {
            const action = () => {
              dispatchStateChange({ type: 'action', value: allowedActions.addVendor });
              dispatchStateChange({ type: 'selectedVendor', value: null });
              dispatchStateChange({ type: 'hasChanges', value: false });
              dispatchStateChange({ type: 'originalVendor', value: null });
            };

            if (checkUnsavedChanges(action)) {
              action();
            }
          }}
          disabled={viewState?.action === allowedActions.addVendor}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button> */}
        <RemoveVendorButton
          clickHandler={handleRemoveVendor}
          action={viewState?.action}
          selectedVendor={viewState?.selectedVendor}
        />
        {/* <button title="Remove Vendor"
          className="btn btn__delete"
          type='button'
          onClick={handleRemoveVendor}
          disabled={viewState?.action === allowedActions.addVendor || viewState.selectedVendor === null}
        >
          <FontAwesomeIcon icon={faMinus} />
        </button> */}
      </div>

      <ReactRouterPrompt when={viewState.hasChanges}>
        {({ isActive, onConfirm, onCancel }) => (
          <>{isActive &&
            <Modal
              title={'Unsaved Changes'}
              description={'You have unsaved changes. Do you want to leave?'}
              yes={{ call: onConfirm }}
              no={{ call: onCancel }}
            />
          }</>
        )}
      </ReactRouterPrompt>
    </CardForm>
  )
}

function AddVendorButton({ clickHandler, action }) {
  const isDisabled = action === allowedActions.addVendor;

  return (
    <button title="Add Vendor" // title={can.add ? 'Create new user' : 'Create new user - no access' }
      className={`btn btn__add ${isDisabled && 'disabled'}`}
      type='button'
      onClick={clickHandler}
      disabled={isDisabled}
    >
      <FontAwesomeIcon icon={faPlus} />
    </button>
  )
}

function RemoveVendorButton({ clickHandler, action, selectedVendor }) {
  const isDisabled = action === allowedActions.addVendor || selectedVendor === null;

  return (
    <button title="Remove Vendor"
      className={`btn btn__delete ${isDisabled && 'disabled'}`}
      type='button'
      onClick={clickHandler}
      disabled={isDisabled}
    >
      <FontAwesomeIcon icon={faMinus} />
    </button>
  )
}

export default Vendors